<script setup>
    // Composables
    import { storeToRefs } from 'pinia';
    import { useWindowStore } from '~/stores/window';
    import { testPassiveSupport } from '@/composables/helpers/client-only/passiveSupport';

    // Props
    const props = defineProps({
        activeSection: {
            type: String,
            required: false,
            default: 'PREMIUM',
        },
        sections: {
            type: Object,
            required: true,
        },
        labels: {
            type: Array,
            required: false,
            default: () => [],
        },
        countObject: {
            type: Object,
        },
        noContainer: {
            type: Boolean,
            default: false,
        },
    });

    // Emits
    const emit = defineEmits(['updateActive']);

    // Variables
    const { innerWidth, deviceIs, scrollY } = storeToRefs(useWindowStore());

    const root = ref(null);
    const lastScrollY = ref(0);
    const scrollingUp = ref(false);
    const stickyNavActive = useState('stickyNavActive', () => false);
    const naturalTop = ref(0);

    watch(scrollY, (newY) => {
        if (!holdActive.value) {
            scrollingUp.value = newY < lastScrollY.value;
        }
        lastScrollY.value = newY;
    });
    const keys = props.sections;
    const holdActive = ref(false);
    const selectedCategory = ref(props.activeSection);
    const categories = props.sections;
    const position = ref({
        left: 0,
        top: 0,
        width: 0,
    });

    // Methods
    const makeLabel = (section) => {
        const sectionIndex = keys.indexOf(section);
        const label = props.labels[sectionIndex] || false;
        if (label) return label;

        return section
            .replace(/_/g, ' ')
            .replace(/-/g, ' ')
            .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    };

    const getDirection = (section) => {
        if (process.server) return;
        const index = keys.indexOf(section);
        const activeIndex = keys.indexOf(props.activeSection);
        return index > activeIndex ? 'down' : 'up';
    };

    const getOffset = (willScrollUp) => {
        if (process.server) return -150;
        const stickyBarHeight = root.value?.offsetHeight || 0;
        const headerHeight = willScrollUp ? (innerWidth.value >= 1024 ? 116 : 52) : 0;
        return -(stickyBarHeight + headerHeight + 16);
    };

    const scrollTo = async (section) => {
        if (process.server) return;
        const element = document.getElementById(section.toLowerCase());
        if (!element) return;

        const targetY = element.getBoundingClientRect().top + window.scrollY;
        const willScrollUp = targetY < window.scrollY;

        holdActive.value = true;
        scrollingUp.value = willScrollUp;

        await nextTick();
        const offsetY = getOffset(willScrollUp);
        const newY = targetY + offsetY;

        window.scrollTo({ top: newY, behavior: 'smooth' });
        window.addEventListener('scrollend', () => {
            holdActive.value = false;
        }, { once: true });

        await nextTick();
        selectedCategory.value = section;
    };

    const updatePosition = () => {
        if (process.server) return;
        if (!root.value) return;

        const activeSectionElement = document.getElementById(props.activeSection.toLowerCase() + '-nav');
        if (!activeSectionElement) return;

        const rectangle = activeSectionElement.getBoundingClientRect();
        const rootRectangle = root.value.getBoundingClientRect();

        position.value = {
            left: rectangle.left - rootRectangle.left,
            top: rectangle.bottom - rootRectangle.bottom,
            width: rectangle.width,
        };
    };

    const handleChange = (event) => {
        const newValue = event.target.value;
        selectedCategory.value = '';
        holdActive.value = true;
        window.addEventListener(
            'scrollend',
            () => {
                holdActive.value = false;
                selectedCategory.value = newValue;
            },
            { once: true }
        );
        updatePosition();
        scrollTo(newValue);
    };

    // Lifecycle
    watch(
        () => props.activeSection,
        (newValue) => {
            if (holdActive.value) return;
            selectedCategory.value = newValue;
            updatePosition();
        }
    );

    function updateStickyState() {
        if (!root.value) return;
        const rect = root.value.getBoundingClientRect();
        const headerHeight = window.innerWidth >= 1024 ? 116 : 52;
        const stuckThreshold = scrollingUp.value ? headerHeight + 2 : 2;
        stickyNavActive.value = rect.top <= stuckThreshold;
    }

    onMounted(() => {
        updatePosition();
        const options = testPassiveSupport() ? { passive: true } : false;

        window.addEventListener('resize', updatePosition, options);
        window.addEventListener('scroll', () => { updatePosition(); updateStickyState(); }, options);
        updateStickyState();
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
        stickyNavActive.value = false;
    });
</script>

<template>
    <section class="generic-sticky-nav" :class="{ container: !noContainer, 'header-visible': scrollingUp }" ref="root">
        <nav>
            <fieldset class="filter-wrapper mobile" :style="`--left:${position.left}px; --top:${position.top}px; --width:${position.width}px;`">
                <legend class="sr-only">Scroll to section</legend>
                <div class="category-wrapper flex flex-col gap-4">
                    <label for="category_type" class="text-(--magenta) font-hubspot-sans text-[14px] font-semibold tracking-[1.4px] uppercase">Section</label>
                    <div class="select relative">
                        <select
                            v-model="selectedCategory"
                            @change="handleChange"
                            id="category_type"
                            class="w-full h-full relative z-1 cursor-pointer appearance-none border border-(--magenta) bg-transparent text-(--magenta) rounded-[50px] py-[0.9455rem] px-5 pr-20 font-hubspot-serif text-[17px] font-normal tracking-[0.34px]">
                            <option v-for="category in categories" :key="category" :value="category">
                                {{ makeLabel(category) }}
                            </option>
                        </select>
                        <MessSvg type="chevron-down" />
                    </div>
                </div>
            </fieldset>
            <ul class="desktop" :style="`--left:${position.left}px; --top:${position.top}px; --width:${position.width}px;`">
                <li v-for="(section, index) in keys" :id="`${section.toLowerCase()}-nav`" :class="activeSection === section ? 'active' : ''" :key="index">
                    <MessLink @click.prevent="scrollTo(section)" :replace="true" :href="`#${section.toLowerCase()}`" :label="makeLabel(section)">
                        <span>
                            <span class="h6 fs-13">{{ makeLabel(section) }}</span>
                            <span v-if="countObject" class="count fs-13">{{ countObject[section] }}</span>
                        </span>
                    </MessLink>
                </li>
            </ul>
        </nav>
    </section>
</template>

<style scoped>

    .filter-wrapper {
        display: flex;
        padding: 1.5rem 1.5rem 1.5rem 0;
        gap: 1.5rem;
    }

    @media (max-width: 1024px) {
        .filter-wrapper {
            flex-direction: column;
            gap: 3rem;
        }
    }

    .filter-wrapper .category-wrapper .select svg {
        z-index: 2;
        pointer-events: none;
        position: absolute;
        right: 1.5rem;
        top: 50%;
        transform: translateY(-50%) scale(0.75);
    }

    .filter-wrapper .category-wrapper .select svg path {
        fill: var(--magenta);
    }

    .generic-sticky-nav {
        transition: top 0.3s ease;
        position: sticky;
        top: 0;
        z-index: 2;
        background: var(--pink);
        border-bottom: 1px solid var(--dark-pink);
    }

    .generic-sticky-nav.header-visible {
        top: 52px;
    }

    @media (min-width: 1024px) {
        .generic-sticky-nav.header-visible {
            top: 116px;
        }
    }

    .generic-sticky-nav nav .mobile {
        display: none !important;
    }

    @media (max-width: 1024px) {
        .generic-sticky-nav nav .mobile {
            display: flex !important;
        }
    }

    .generic-sticky-nav nav ul {
        display: flex;
        justify-content: flex-start;
        position: relative;
        flex-wrap: wrap;
        gap: 30px;
        padding: 13px 0;
    }

    .generic-sticky-nav nav ul li {
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    .generic-sticky-nav nav ul li a {
        color: var(--magenta);
        transition: opacity 0.2s ease;
        text-decoration: none;
    }

    .generic-sticky-nav nav ul li a > span {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
    }

    .generic-sticky-nav nav ul li a .h6 {
        font-family: var(--font-hubspot-serif);
        font-size: 15px;
        font-weight: 400;
        line-height: 1.4;
        letter-spacing: 0.3px;
        text-transform: none;
        color: var(--magenta);
    }

    .generic-sticky-nav nav ul li a .count {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        border-radius: 100px;
        background: #eeb4d0;
        padding: 0 4px;
        font-family: var(--font-hubspot-sans);
        font-size: 10px;
        font-weight: 400;
        line-height: 1.4;
        letter-spacing: 0.2px;
        color: var(--magenta);
    }

    .generic-sticky-nav nav ul li.active a .count {
        background-color: white;
    }

    @media (hover: hover) {
        .generic-sticky-nav nav ul li a:hover .h6 {
            text-decoration: underline dotted #a55c87;
            text-underline-offset: 3px;
            text-decoration-thickness: 8.5%;
        }

        .generic-sticky-nav nav ul li:hover .count {
            background-color: white;
        }
    }

    .generic-sticky-nav nav ul li.active a .h6 {
        text-decoration: underline dotted #a55c87;
        text-underline-offset: 3px;
        text-decoration-thickness: 8.5%;
    }

    @media (max-width: 1024px) {
        .generic-sticky-nav nav .desktop {
            display: none;
        }
    }

    @media (max-width: 768px) {
        .generic-sticky-nav nav ul {
            gap: 20px;
        }

        .generic-sticky-nav nav ul li a .h6 {
            font-size: 12px;
        }

        .generic-sticky-nav nav ul li a .count {
            width: 16px;
            height: 16px;
            font-size: 9px;
        }
    }
</style>
